/**
 * Configuration for automatic cleanup of soft-deleted documents in RxDB local database.
 *
 * Soft-deleted documents (isDeleted=true) are retained for a configured period
 * before being permanently removed from the local database. This helps with:
 * - Storage management: Reduce local database size
 * - Privacy compliance: Support data retention policies
 * - Performance: Fewer documents to query
 */

export interface PurgeConfig {
  /**
   * Number of days to retain soft-deleted documents before purging
   * Default: 30 days
   */
  retentionDays: number

  /**
   * Interval (in milliseconds) between purge runs
   * Default: 6 hours
   */
  runIntervalMs: number

  /**
   * Whether purge is enabled
   * Default: true
   */
  enabled: boolean
}

/**
 * Default purge configuration
 */
export const DEFAULT_PURGE_CONFIG: PurgeConfig = {
  retentionDays: 30,
  runIntervalMs: 1000 * 60 * 60 * 6, // 6 hours
  enabled: true,
}

/**
 * Collections that should be purged
 * Matches the actual RxDB collections defined in MolyDatabaseCollections
 */
export const PURGEABLE_COLLECTIONS = [
  'tasks',
  'taskcontent',
  'labels',
  'labelgroups',
  'tasklabels',
  'comments',
  'reminders',
  'notifications',
  'projects',
  'spaces',
  'statuses',
  'members',
  'activities',
  'reactions',
  'checklists',
  'checklistitems',
  'spaceusers',
  'mentions',
  'attachments',
  'subscriptions',
  // plans and usage don't have soft delete fields, so excluded
] as const

export type PurgeableCollection = (typeof PURGEABLE_COLLECTIONS)[number]

/**
 * Get purge configuration from environment or use defaults
 */
export function getPurgeConfig(): PurgeConfig {
  // Can be overridden via environment variables if needed
  const retentionDays = import.meta.env.VITE_PURGE_RETENTION_DAYS
    ? parseInt(import.meta.env.VITE_PURGE_RETENTION_DAYS, 10)
    : DEFAULT_PURGE_CONFIG.retentionDays

  const runIntervalMs = import.meta.env.VITE_PURGE_INTERVAL_MS
    ? parseInt(import.meta.env.VITE_PURGE_INTERVAL_MS, 10)
    : DEFAULT_PURGE_CONFIG.runIntervalMs

  const enabled = import.meta.env.VITE_PURGE_ENABLED
    ? import.meta.env.VITE_PURGE_ENABLED === 'true'
    : DEFAULT_PURGE_CONFIG.enabled

  return {
    retentionDays,
    runIntervalMs,
    enabled,
  }
}

/**
 * Calculate the cutoff timestamp for purging
 * Documents with deletedOn < cutoff should be purged
 */
export function getPurgeCutoffTimestamp(retentionDays: number): number {
  const now = Date.now()
  const retentionMs = retentionDays * 24 * 60 * 60 * 1000
  return now - retentionMs
}
